import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity, DocumentType } from "@erp/shared-types";

/**
 * Document type enum for splitting config
 */
const documentTypeEnum = z.nativeEnum(DocumentType);

/**
 * Split Documents Step
 *
 * Uses Extend AI's splitter processor to split a multi-document file into
 * individual child documents before classification.
 *
 * For each document associated with the inbound email:
 * - Calls Extend AI to detect and extract individual document splits
 * - Creates child Document records for each matching split
 * - Marks the parent document as MULTI_DOC_PARENT so downstream
 *   classification and OCR steps skip it automatically
 *
 * Documents that cannot be split (no matching splits, processor error, etc.)
 * are left unchanged and will flow through classification normally.
 */
export const splitDocumentsStep = defineStepConfig({
  type: "split_documents",
  name: "Split Documents",
  description:
    "Split multi-document files into individual child documents using Extend AI",
  category: "Transformation",
  deprecated: {
    reason:
      "Use Split Document V2 which supports user-defined classifications and is provider-agnostic",
    replacedBy: "split_document_v2",
  },

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email whose document attachments should be split",
    ),
  }),

  outputSchema: z.object({
    documents: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .describe(
        "The child documents created by splitting the multi-document file",
      ),
  }),

  configSchema: z.object({
    documentTypes: z
      .array(documentTypeEnum)
      .optional()
      .describe(
        "Document types to extract during splitting. Only splits matching these types are kept as child records. Defaults to [PURCHASE_ORDER].",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "3 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
